import { isNil } from 'ramda';
import { IGetVillageListResponseSchema } from '../../node/repositories/countryside-search/entities/entities/GetVillageListResponseSchema';
import { isArrayWithItems } from './helpers';
import { IStationModel, IStations } from '../store/stations';
import { getStationNameVariantsWithType } from '../store/stations/actions';
import { normalizeNameForSearch, stationTypes } from './geo';
import { filterSelectedCommute } from './roads';
import { groupBy } from './groupBy';
import { uniqBy } from './uniqBy';
import { IRailway, IRailwayDirection } from '../types/geo';

export const STATION_DISTANCE_GROUPS = [10, 40, 70, 100, 130, 160, 190];

export const getStationGroupsLabel = (first?: number, last?: number): string => {
  if (first === undefined && last === undefined) {
    return '';
  } else if (first === undefined) {
    return `До ${last} км`;
  } else if (last === undefined) {
    return `Больше ${first} км`;
  } else {
    return `${first}—${last} км`;
  }
};

export const getStationGroups = (groups: number[] = STATION_DISTANCE_GROUPS) => {
  const result = [];
  if (isArrayWithItems(groups)) {
    for (let i = 0; i <= groups.length; i++) {
      result.push(getStationGroupsLabel(groups[i - 1], groups[i]));
    }
  }

  return result;
};

export const DEFAULT_STATION_GROUPS = getStationGroups();

export const getStationGroupIndex = (station: IStationModel, groups = STATION_DISTANCE_GROUPS): number => {
  if (!isArrayWithItems(groups)) {
    return -1;
  }
  const result = groups.findIndex(distance => station.sorterId <= distance);

  return result < 0 ? groups.length : result;
};

export const setStationGroup = (station: IStationModel): IStationModel => ({
  ...station,
  distanceGroup: getStationGroupIndex(station),
});

export const getDirectionStations = (stations: IStationModel[], direction: number) =>
  stations.filter(({ directions }) => directions.includes(direction));

export const getDirectionGroupedStations = (stations: IStationModel[], direction = -1) =>
  groupBy(getDirectionStations(stations, direction), 'distanceGroup');

export const getSortedDirections = (directions: IRailwayDirection[]) => {
  const result = uniqBy(directions, 'id');
  result.sort((a: IRailwayDirection, b: IRailwayDirection) => {
    if (!a.name || !b.name) {
      return 0;
    }

    return a.name.localeCompare(b.name);
  });

  return result;
};

export const getSelectedDirections = (directions: IRailwayDirection[], selected: number[]) =>
  directions.filter(({ id }) => !isNil(id) && selected.includes(id));

export const getDefaultDirection = (directions: IRailwayDirection[] = [], selected: number[] = []) => {
  // Если станция уже выбрана, а направление нет, то выбрано направление соответствующее станции.
  const selectedDirections = getSelectedDirections(directions, selected);

  return (selectedDirections[0] && selectedDirections[0].id) || (directions[0] && directions[0].id) || -1;
};

export const filterSelectedStations = (ids: number[]) => (value: { id: number }) => ids.includes(value.id);

export const isStationEnabled = (
  id: number,
  stationData: IGetVillageListResponseSchema['stationData'],
  stationDistance: number,
) => {
  const stationDataValue = (stationData as Record<number, number>)[id];

  return stationDataValue <= stationDistance;
};

export const getStationModels = (stations: IStations): IStationModel[] => {
  const { currentStations, selectedStations } = stations;

  return currentStations.filter(filterSelectedCommute(selectedStations));
};

export const getStationNameVariants = (station: IRailway): string[] =>
  stationTypes
    .reduce((acc, value) => acc.concat(getStationNameVariantsWithType(station, value)), [station.name])
    .map(normalizeNameForSearch);

export const doesStationMatchRequest = (station: IRailway, value: string): boolean =>
  getStationNameVariants(station).some(variant => variant.includes(value));

export const getStationById = (stations: IStationModel[], stationId: number): IStationModel | undefined =>
  stations.find(station => station.id === stationId);

export const getDirectionsName = (directions: IRailwayDirection[]) => {
  const selectedDirectionNames = directions.reduce(
    (result: string[], { name }) => (name ? result.concat(name) : result),
    [],
  );

  return `${selectedDirectionNames.join(', ')}`;
};
